import React, { useEffect, useState, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import {
  MdAdd,
  MdDelete,
  MdOpenInNew,
} from "react-icons/md";
import { FiFolder, FiTag } from "react-icons/fi";
import TableNavTabs from "./TableNavTabs";
import DynamicTable from "../../Components/Table/DynamicTable";
import Loader from "../../Components/Loader/Loader";
import {
  fetchProjects,
  addProject,
  updateProject,
  fetchProjectDetails,
  addTypeInProject,
  clearStatusMessage,
} from "../../Redux/Slice/TableManagementSlice";

const ProjectsMaster = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const {
    projects,
    projectsLoading,
    projectDetails,
    projectDetailsLoading,
    statusMessage,
    actionLoading,
    actionError,
  } = useSelector((state) => state.TableManagement);

  const [projectName, setProjectName] = useState("");
  const [updateName, setUpdateName] = useState("");
  const [selectedRow, setSelectedRow] = useState(null);

  // Initial load
  useEffect(() => {
    dispatch(fetchProjects());
  }, [dispatch]);

  // Handle toast notifications
  useEffect(() => {
    if (statusMessage) {
      toast.success(statusMessage);
      dispatch(clearStatusMessage());
    }
    if (actionError) {
      toast.error(actionError);
      dispatch(clearStatusMessage());
    }
  }, [statusMessage, actionError, dispatch]);

  // Sync update input when row selection changes
  useEffect(() => {
    if (selectedRow) {
      setUpdateName(selectedRow.projectName || "");
    } else {
      setUpdateName("");
    }
  }, [selectedRow]);

  // Main projects grid rows
  const projectRows = useMemo(() => {
    const list = projects?.data || [];
    return list.map((item, index) => ({
      id: index + 1,
      projectName: item?.project_name,
      NoType: item?.Type_id?.length || 0,
      Mid: item?._id,
    }));
  }, [projects]);

  // Types currently assigned to this project
  const assignedTypesRows = useMemo(() => {
    const typeList = projectDetails?.All_Type?.Type_id || [];
    return typeList.map((item, index) => ({
      id: index + 1,
      TypeName: item?.id?.Type_name || "Unnamed Type",
      Mid: item?.id?._id || item?._id,
    }));
  }, [projectDetails]);

  // Available types that can be added
  const availableTypesRows = useMemo(() => {
    const filterList = projectDetails?.Fillter_Type || [];
    return filterList.map((item, index) => ({
      id: index + 1,
      TypeName: item?.Type_name || "Unnamed Type",
      Mid: item?._id,
    }));
  }, [projectDetails]);

  // Row selection handler
  const handleRowClick = (row) => {
    const activeRow = row?.row || row;
    setSelectedRow(activeRow);
    if (activeRow?.Mid) {
      dispatch(fetchProjectDetails(activeRow.Mid));
    }
  };

  // Add Project
  const handleAddSubmit = (e) => {
    e?.preventDefault();
    if (!projectName.trim()) {
      toast.warn("Please enter a project name");
      return;
    }
    dispatch(addProject(projectName.trim()));
    setProjectName("");
  };

  // Update Project Name
  const handleUpdateSubmit = (e) => {
    e?.preventDefault();
    if (!updateName.trim()) {
      toast.warn("Please enter updated project name");
      return;
    }
    if (!selectedRow?.Mid) return;

    dispatch(updateProject({ id: selectedRow.Mid, projectName: updateName.trim() }));
    setSelectedRow((prev) => (prev ? { ...prev, projectName: updateName.trim() } : null));
  };

  // Add / Remove Type from Project
  const handleToggleType = (typeId) => {
    if (!selectedRow?.Mid || !typeId) return;
    dispatch(
      addTypeInProject({
        projectId: selectedRow.Mid,
        typeId,
      })
    );
  };

  // Main columns for DynamicTable
  const projectColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "projectName",
      headerName: "Project Name",
      flex: 2,
      renderCell: (params) => (
        <span className="font-semibold text-gray-800 flex items-center gap-2">
          <FiFolder className="text-[#133a69]" />
          {params.row.projectName}
        </span>
      ),
    },
    {
      field: "NoType",
      headerName: "Assigned Types",
      flex: 1,
      renderCell: (params) => (
        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-semibold bg-[#E8F2FF] text-[#133a69]">
          {params.row.NoType} Types
        </span>
      ),
    },
    {
      field: "actions",
      headerName: "Action",
      flex: 1,
      sortable: false,
      renderCell: (params) => {
        const isSelected = selectedRow?.Mid === params.row.Mid;
        return (
          <div
            className="flex items-center gap-2"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              onClick={() => handleRowClick(params.row)}
              className={`px-3 py-1 text-xs font-semibold rounded transition ${
                isSelected
                  ? "bg-[#133a69] text-white"
                  : "bg-gray-200 hover:bg-[#133a69] hover:text-white text-gray-800"
              }`}
            >
              {isSelected ? "Selected" : "Select"}
            </button>
            <button
              onClick={() => navigate("/TypeMaster")}
              title="Jump to Type Master"
              className="text-gray-500 hover:text-[#133a69] p-1"
            >
              <MdOpenInNew size={16} />
            </button>
          </div>
        );
      },
    },
  ];

  // Assigned types columns for DynamicTable
  const assignedTypesColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "actions",
      headerName: "Remove",
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleToggleType(params.row.Mid)}
            className="bg-red-600 text-white p-1 rounded hover:bg-red-700 transition"
            title="Remove from project"
          >
            <MdDelete size={16} />
          </button>
        </div>
      ),
    },
    {
      field: "TypeName",
      headerName: "Type Name",
      flex: 2,
      renderCell: (params) => (
        <span className="flex items-center gap-2 text-gray-800 font-medium">
          <FiTag className="text-red-500" />
          {params.row.TypeName}
        </span>
      ),
    },
  ];

  // Available types columns for DynamicTable
  const availableTypesColumns = [
    { field: "id", headerName: "ID", flex: 0.4 },
    {
      field: "actions",
      headerName: "Add",
      flex: 0.6,
      sortable: false,
      renderCell: (params) => (
        <div onClick={(e) => e.stopPropagation()}>
          <button
            onClick={() => handleToggleType(params.row.Mid)}
            className="All_button text-white p-1 rounded hover:opacity-90 transition"
            title="Add to project"
          >
            <MdAdd size={16} />
          </button>
        </div>
      ),
    },
    {
      field: "TypeName",
      headerName: "Type Name",
      flex: 2,
      renderCell: (params) => (
        <span className="flex items-center gap-2 text-gray-800 font-medium">
          <FiTag className="text-green-600" />
          {params.row.TypeName}
        </span>
      ),
    },
  ];

  return (
    <div>
      {/* Page Title & Navigation Tabs */}
      <TableNavTabs title="Projects Master" />

      {/* Main Grid: Form Left, Projects Table Right */}
      <div className="grid grid-cols-12 gap-4">
        {/* Form Section */}
        <div className="col-span-12 lg:col-span-5">
          <div className="Form_bg_color bg-white shadow-md rounded-md p-4">
            <div className="mb-4">
              <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                Project Details
              </h3>
            </div>

            {/* Create Project Form */}
            <form onSubmit={handleAddSubmit} className="space-y-3">
              <div>
                <label className="font-semibold block mb-1 text-sm text-gray-700">
                  Project Name <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-2">
                  <input
                    type="text"
                    placeholder="Enter Project Name"
                    value={projectName}
                    onChange={(e) => setProjectName(e.target.value)}
                    className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded text-sm outline-none focus:border-[#133a69]"
                  />
                  <button
                    type="submit"
                    disabled={actionLoading || !projectName.trim()}
                    className="All_button text-white px-6 py-2 rounded transition hover:opacity-90 font-medium text-sm whitespace-nowrap"
                  >
                    Submit
                  </button>
                </div>
              </div>
            </form>

            {/* Update Project Section */}
            {selectedRow ? (
              <div className="mt-4 pt-4 border-t border-gray-200 animate__animated animate__fadeIn">
                <div className="flex items-center justify-between mb-2">
                  <span className="font-semibold text-xs text-[#133a69] uppercase tracking-wider">
                    Update Selected Project
                  </span>
                  <button
                    type="button"
                    onClick={() => setSelectedRow(null)}
                    className="text-xs text-red-600 hover:underline font-semibold"
                  >
                    Clear Selection
                  </button>
                </div>
                <div className="text-xs text-gray-600 mb-2">
                  Selected: <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded">{selectedRow.projectName}</span>
                </div>
                <div>
                  <label className="font-semibold block mb-1 text-sm text-gray-700">
                    Update Project Name <span className="text-red-500">*</span>
                  </label>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Enter Updated Project Name"
                      value={updateName}
                      onChange={(e) => setUpdateName(e.target.value)}
                      className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded text-sm outline-none focus:border-[#133a69]"
                    />
                    <button
                      type="button"
                      onClick={handleUpdateSubmit}
                      disabled={actionLoading || !updateName.trim()}
                      className="All_button text-white px-6 py-2 rounded transition hover:opacity-90 font-medium text-sm whitespace-nowrap"
                    >
                      Update
                    </button>
                  </div>
                </div>
              </div>
            ) : (
              <div className="mt-4 p-3 bg-gray-50 border border-dashed border-gray-300 rounded text-xs text-gray-500">
                Click any project row in the table to edit its name or manage assigned types.
              </div>
            )}
          </div>
        </div>

        {/* Projects Table Section */}
        <div className="col-span-12 lg:col-span-7">
          <div className="Form_bg_color bg-white shadow-md rounded-md p-4">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                All Projects
              </h3>
              <span className="bg-[#112A47] text-white px-4 py-2 text-sm font-bold rounded shadow">
                Total: {projectRows.length}
              </span>
            </div>
            {projectsLoading ? (
              <div className="p-8 flex justify-center">
                <Loader />
              </div>
            ) : (
              <DynamicTable
                columns={projectColumns}
                rows={projectRows}
                height={380}
                searchable={true}
                onRowClick={handleRowClick}
                getRowClassName={(row) =>
                  row.Mid === selectedRow?.Mid ? "!bg-blue-100 font-bold" : ""
                }
              />
            )}
          </div>
        </div>
      </div>

      {/* Detail Section: Shows Assigned & Available Types when a Project is selected */}
      {selectedRow ? (
        <div className="Form_bg_color bg-white shadow-md rounded-md p-4 mt-4 animate__animated animate__fadeIn">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 border-b pb-3 mb-4">
            <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
              Types in Project: {selectedRow.projectName}
            </h3>
            <div className="flex items-center gap-2">
              <button
                onClick={() => navigate("/TypeMaster")}
                className="All_button text-white px-4 py-2 rounded text-xs font-semibold hover:opacity-90 transition flex items-center gap-1 shadow"
              >
                Jump to Type Master <MdOpenInNew size={14} />
              </button>
              <button
                onClick={() => setSelectedRow(null)}
                className="bg-gray-400 text-white px-4 py-2 rounded text-xs font-semibold hover:bg-gray-500 transition shadow"
              >
                Close
              </button>
            </div>
          </div>

          {projectDetailsLoading ? (
            <div className="p-8 flex justify-center">
              <Loader />
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <h4 className="font-semibold text-sm mb-2 text-gray-700">
                  Types In Project ({assignedTypesRows.length})
                </h4>
                <DynamicTable
                  columns={assignedTypesColumns}
                  rows={assignedTypesRows}
                  height={300}
                  searchable={true}
                />
              </div>
              <div>
                <h4 className="font-semibold text-sm mb-2 text-gray-700">
                  Available Types to Add ({availableTypesRows.length})
                </h4>
                <DynamicTable
                  columns={availableTypesColumns}
                  rows={availableTypesRows}
                  height={300}
                  searchable={true}
                />
              </div>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
};

export default ProjectsMaster;
